// @ts-nocheck
/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.3
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2024 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useState } from "react";

// @mui material components
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";

import appleLogo from "assets/images/small-logos/logo-apple.svg";
import googlePlayTriangle from "assets/images/google-play-triangle.svg";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import { useLocale } from "lib/i18n/client";
import { getTranslations } from "lib/i18n/dictionary";
import { trackAdminAnalyticsEvent } from "lib/marketing-analytics";

// Custom styles for the SidenavCard
import { card, cardContent, cardIconBox, cardIcon } from "examples/Sidenav/styles/sidenavCard";

// Soft UI Dashboard PRO React context
import { useSoftUIController } from "context";

const APP_DOWNLOAD_PROMO_DISMISSED_STORAGE_KEY = "admin-app-download-promo-dismissed";

function SidenavCard() {
  const [controller] = useSoftUIController();
  const { miniSidenav, sidenavColor } = controller;
  const locale = useLocale();
  const translations = getTranslations(locale);
  const [isDismissed, setIsDismissed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    setIsDismissed(window.localStorage.getItem(APP_DOWNLOAD_PROMO_DISMISSED_STORAGE_KEY) === "true");
  }, []);

  const handleDismiss = () => {
    setIsDismissed(true);

    if (typeof window !== "undefined") {
      window.localStorage.setItem(APP_DOWNLOAD_PROMO_DISMISSED_STORAGE_KEY, "true");
    }
  };

  const appStoreUrl = process.env.REACT_APP_APP_STORE_URL?.trim() || "";
  const googlePlayUrl = process.env.REACT_APP_GOOGLE_PLAY_URL?.trim() || "";

  const trackDownloadClick = (store: "app_store" | "google_play") => {
    trackAdminAnalyticsEvent("admin_sidebar_app_download_click", {
      event_category: "admin_sidebar",
      location: "sidebar_banner",
      store,
    });
  };

  if (isDismissed) {
    return null;
  }

  return (
    <Card
      sx={(theme) => card(theme, { miniSidenav })}
      data-testid="admin-sidebar-promo-card"
      role="status"
    >
      <CardContent sx={(theme) => cardContent(theme, { sidenavColor })}>
        <SoftBox display="flex" justifyContent="space-between" alignItems="flex-start" gap={1}>
          <SoftBox
            bgColor="white"
            width="2rem"
            height="2rem"
            borderRadius="md"
            shadow="md"
            sx={cardIconBox}
          >
            <Icon fontSize="medium" sx={(theme) => cardIcon(theme, { sidenavColor })}>
              campaign
            </Icon>
          </SoftBox>
          <IconButton
            size="small"
            onClick={handleDismiss}
            aria-label={translations.adminChrome.summerPromoDismiss}
            data-testid="admin-sidebar-promo-dismiss"
            sx={(theme) => ({
              color: theme.palette.white.main,
              p: 0.25,
              mt: -0.5,
              mr: -0.5,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.12)",
              },
              "& .MuiIcon-root": {
                color: theme.palette.white.main,
              },
            })}
          >
            <Icon fontSize="small">close</Icon>
          </IconButton>
        </SoftBox>
        <SoftBox lineHeight={1.2} mt={2} sx={{ minWidth: 0 }}>
          <SoftTypography
            variant="button"
            color="white"
            fontWeight="bold"
            sx={{
              display: "block",
              whiteSpace: "normal",
              overflowWrap: "anywhere",
            }}
          >
            {translations.adminChrome.appDownloadBanner}
          </SoftTypography>
        </SoftBox>
        <SoftBox
          mt={2}
          display="flex"
          flexDirection="column"
          gap={0.75}
          data-testid="admin-sidebar-promo-actions"
        >
          <SoftButton
            component="a"
            href={appStoreUrl}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackDownloadClick("app_store")}
            variant="contained"
            color="dark"
            fullWidth
            data-testid="admin-sidebar-promo-app-store"
            aria-label={translations.adminChrome.appDownloadAppStoreCta}
            sx={{
              minHeight: 40,
              borderRadius: "0.65rem",
              backgroundColor: "#000",
              color: "#fff",
              boxShadow: "none",
              px: 1.5,
              textTransform: "none",
              textAlign: "center",
              "&:hover": {
                backgroundColor: "#000",
                boxShadow: "none",
                transform: "none",
              },
              "&:focus, &:active": {
                backgroundColor: "#000",
                boxShadow: "none",
                transform: "none",
              },
            }}
          >
            <SoftBox
              display="grid"
              alignItems="center"
              width="100%"
              gridTemplateColumns="16px minmax(0, 1fr) 16px"
              columnGap={0.5}
            >
              <SoftBox
                component="img"
                src={appleLogo}
                alt=""
                aria-hidden="true"
                sx={{
                  width: 16,
                  height: 16,
                  flexShrink: 0,
                  filter: "brightness(0) invert(1)",
                  justifySelf: "center",
                }}
              />
              <SoftTypography
                color="white"
                fontWeight="medium"
                sx={{
                  display: "block",
                  width: "100%",
                  fontSize: "0.74rem",
                  lineHeight: 1.1,
                  letterSpacing: "0.01em",
                  textAlign: "center",
                }}
              >
                {translations.adminChrome.appDownloadAppStoreCta}
              </SoftTypography>
              <SoftBox aria-hidden="true" sx={{ width: 14, height: 14 }} />
            </SoftBox>
          </SoftButton>
          <SoftButton
            component="a"
            href={googlePlayUrl}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackDownloadClick("google_play")}
            variant="contained"
            color="white"
            fullWidth
            data-testid="admin-sidebar-promo-google-play"
            aria-label={translations.adminChrome.appDownloadGooglePlayCta}
            sx={{
              minHeight: 40,
              borderRadius: "0.65rem",
              backgroundColor: "#fff",
              color: "#000",
              boxShadow: "none",
              px: 1.5,
              textTransform: "none",
              textAlign: "center",
              "&:hover": {
                backgroundColor: "#fff",
                boxShadow: "none",
                transform: "none",
              },
              "&:focus, &:active": {
                backgroundColor: "#fff",
                boxShadow: "none",
                transform: "none",
              },
            }}
          >
            <SoftBox
              display="grid"
              alignItems="center"
              width="100%"
              gridTemplateColumns="16px minmax(0, 1fr) 16px"
              columnGap={0.5}
            >
              <SoftBox
                component="img"
                src={googlePlayTriangle}
                alt=""
                aria-hidden="true"
                sx={{
                  width: 16,
                  height: 16,
                  flexShrink: 0,
                  justifySelf: "center",
                }}
              />
              <SoftTypography
                color="dark"
                fontWeight="medium"
                sx={{
                  display: "block",
                  width: "100%",
                  fontSize: "0.74rem",
                  lineHeight: 1.1,
                  letterSpacing: "0.01em",
                  textAlign: "center",
                }}
              >
                {translations.adminChrome.appDownloadGooglePlayCta}
              </SoftTypography>
              <SoftBox aria-hidden="true" sx={{ width: 14, height: 14 }} />
            </SoftBox>
          </SoftButton>
        </SoftBox>
      </CardContent>
    </Card>
  );
}

export default SidenavCard;
